import { useAllCvletters } from "http-hooks/cvletter/useCvletter";
import DeleteCvletter from "components/cvletter/DeleteCvletter";
import PrintCvletter from "components/cvletter/PrintCvletter";
import { useState } from "react";
import Modal from "components/ui/modals";
import { formatDateAndTime } from "utils/date";
import { Table, THead, TBody, TR, TH, TD, ActionButton } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import Pagination from "components/ui/Pagination";
import {
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function Cvletter() {
  const navigate = useNavigate();
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [printItemId, setPrintItemId] = useState(null);
  const [filter, setFilter] = useState({
    refid: "",
    page: 1,
    limit: 10,
  });
  const [searchCvletter, setSearchCvletter] = useState("");

  const handleEdit = (id) => {
    // URL encode the id to handle slashes in the refid
    navigate(`/cvletter/${encodeURIComponent(id)}`);
  };
  
  const handleDelete = (id) => {
    setDeleteItemId(id);
  };
  
  const handlePrint = (id) => {
    setPrintItemId(id);
  };

  const handleCvletterSearch = (searchTerm) => {
    setFilter(prev => ({
      ...prev,
      refid: searchTerm
    }));
  };

  const handlePageChange = (page) => {
    setFilter(prev => ({
      ...prev,
      page: page
    }));
  };

  const {
    data: { data: allcvletters, page: currentPage, totalPages } = [],
  } = useAllCvletters(filter);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Delete Modal */}
      {deleteItemId && (
        <Modal open={!!deleteItemId} setOpen={(val) => !val && setDeleteItemId(null)}>
          <DeleteCvletter id={deleteItemId} onClose={() => setDeleteItemId(null)} />
        </Modal>
      )}

      {/* Print Modal */}
      {printItemId && (
        <Modal open={!!printItemId} setOpen={(val) => !val && setPrintItemId(null)}>
          <PrintCvletter refid={printItemId} onClose={() => setPrintItemId(null)} />
        </Modal>
      )}

      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            Covering Letter
          </h1>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => navigate("/cvletter/new")}
            className="block rounded-md bg-gray-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          >
            Add Covering Letter
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg">
          <InputSearch
            value={searchCvletter}
            onChange={(e) => setSearchCvletter(e.target.value)}
            onSearch={handleCvletterSearch}
            label="Reference ID"
            placeholder="Search by Reference ID"
          />
        </div>
      </div>

      {/* Clear Filters Row */}
      <div className="grid grid-cols-1 gap-3 mb-6">
        <div className="bg-white rounded-lg flex items-center justify-center">
          <button
            type="button"
            onClick={() => {
              setSearchCvletter("");
              setFilter({
                refid: "",
                page: 1,
                limit: 10,
              });
            }}
            className="w-full rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <Table>
        <THead>
          <TR>
            <TH first>Refid</TH>
            <TH>Date</TH>
            <TH>Vendor</TH>
            <TH>Remark</TH>
            <TH last>
              <span className="sr-only">Action</span>
            </TH>
          </TR>
        </THead>
        <TBody>
          {allcvletters?.map((cvletter) => (
            <TR key={cvletter.refid}>
              <TD first>{cvletter.refid}</TD>
              <TD>{formatDateAndTime(cvletter.date)}</TD>
              <TD>
                {cvletter.vendor ? (
                  <div className="font-medium">{cvletter.vendor.name}</div>
                ) : (
                  cvletter.vendid || '-'
                )}
              </TD>
              <TD>{cvletter.remark}</TD>
              <TD last>
                <ActionButton
                  onClick={() => handlePrint(cvletter.refid)}
                  variant="primary"
                  title="View/Print"
                >
                  <EyeIcon className="h-4 w-4" />
                  <span className="sr-only">View/Print, {cvletter.refid}</span>
                </ActionButton>
                <ActionButton
                  onClick={() => handleEdit(cvletter.refid)}
                  variant="secondary"
                  title="Edit"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  <span className="sr-only">Edit, {cvletter.refid}</span>
                </ActionButton>
                <ActionButton
                  onClick={() => handleDelete(cvletter.refid)}
                  variant="danger"
                  title="Delete"
                  className="mr-0"
                >
                  <TrashIcon className="h-4 w-4" />
                  <span className="sr-only">Delete, {cvletter.refid}</span>
                </ActionButton>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showPageJump={true}
      />
    </div>
  );
}
